/**
 * This file contains a collection of utils that modify a hot instance directly and also
 * fire Milanote-specific HOT hooks so the active-table session can schedule persistence.
 *
 * As such they work on the active hot table only.
 */

import { cloneDeep, get, isEqual } from 'lodash';

import { TextAlignment, VerticalAlignment } from '../../../../../../../common/table/CellTypeConstants';
import { CellData, CellDataChange, TableChangeSource } from '../../../../../../../common/table/TableTypes';
import {
    ON_MILANOTE_MARK_DIRTY,
    ON_MILANOTE_MARK_DIRTY_AND_FLUSH,
} from '../../../sync/milanoteHotToReduxSessionHookNames';
import { MNHotInstance } from '../../../tableHotInstanceTypes';
import { getAllCellsBetween } from '../../selectionUtils/tableSelectionGeometryUtils';
import { buildHotTableContentData } from '../hotTableDataUtils';
import { applyExactHotTableCellDataChanges, buildHotTableTextStyleUpdate } from '../hotTableUpdateUtils';
import { addHotTableCellDataUndoAction } from '../undoRedo/hotTableUndoRedoMetaCellDataUtils';

const EMPTY_CELL_DATA: CellData = { value: null };

/**
 * Returns the stored cell data for a coordinate, falling back to an empty cell shape when metadata is missing.
 */
const getHotTableCellDataForChange = (hotTableData: CellData[][], row: number, col: number): CellData =>
    cloneDeep(get(hotTableData, [row, col]) || EMPTY_CELL_DATA);

/**
 * Applies cell-data changes to the active HOT instance and schedules a redux session flush.
 */
export const updateHotTableCellData = (
    hotInstance: MNHotInstance,
    cellDataChanges: CellDataChange[],
    tableUpdateSource: TableChangeSource,
): void => {
    if (!cellDataChanges.length) return;

    addHotTableCellDataUndoAction(hotInstance, cellDataChanges, tableUpdateSource);
    applyExactHotTableCellDataChanges(hotInstance, cellDataChanges, tableUpdateSource);

    hotInstance.runHooks(ON_MILANOTE_MARK_DIRTY, tableUpdateSource);
};

/**
 * Applies the provided cell-data transform across the active HOT selection.
 */
export const updateHotTableSelectedCellData = (
    hotInstance: MNHotInstance,
    cellDataUpdateFn: (prevCellData: CellData, row?: number, col?: number) => CellData,
    tableUpdateSource: TableChangeSource,
    forceUpdate = false,
): void => {
    const cellSelections = hotInstance?.getSelected();
    const hotTableData = buildHotTableContentData(hotInstance);

    const cellDataChanges = getAllCellsBetween(cellSelections).reduce<CellDataChange[]>((changes, { row, col }) => {
        const previousCellData = getHotTableCellDataForChange(hotTableData, row, col);
        const newCellData = cellDataUpdateFn(cloneDeep(previousCellData), row, col);

        if (!forceUpdate && isEqual(previousCellData, newCellData)) return changes;

        changes.push([row, col, previousCellData, newCellData]);
        return changes;
    }, []);

    updateHotTableCellData(hotInstance, cellDataChanges, tableUpdateSource);
};

/**
 * Updates the text alignment for the selected cells in the active HOT instance.
 */
export const updateHotTableSelectedCellTextAlignment = (
    hotInstance: MNHotInstance,
    textAlignment: TextAlignment,
): void =>
    updateHotTableSelectedCellData(
        hotInstance,
        (cellData: CellData) => ({
            ...cellData,
            textAlignment,
        }),
        TableChangeSource.CELL_META_UPDATE_CELL_TEXT_ALIGNMENT,
    );

/**
 * Updates the vertical alignment for the selected cells in the active HOT instance.
 */
export const updateHotTableSelectedCellVerticalAlignment = (
    hotInstance: MNHotInstance,
    verticalAlignment: VerticalAlignment,
): void =>
    updateHotTableSelectedCellData(
        hotInstance,
        (cellData: CellData) => ({
            ...cellData,
            verticalAlignment,
        }),
        TableChangeSource.CELL_META_UPDATE_CELL_VERTICAL_ALIGNMENT,
    );

/**
 * Updates the background colour for the selected cells in the active HOT instance.
 */
export const updateHotTableSelectedCellBackgroundColor = (
    hotInstance: MNHotInstance,
    backgroundColor: string | null,
    forceUpdate = false,
): void =>
    updateHotTableSelectedCellData(
        hotInstance,
        (cellData: CellData) => ({
            ...cellData,
            background: backgroundColor,
        }),
        TableChangeSource.CELL_META_UPDATE_CELL_BACKGROUND,
        forceUpdate,
    );

/**
 * Applies or removes a text style on the selected cells in the active HOT instance.
 */
export const updateHotTableSelectedCellTextStyle = (hotInstance: MNHotInstance, textStyle: string | null): void => {
    const cellDataChanges = buildHotTableTextStyleUpdate(hotInstance, textStyle);
    if (!cellDataChanges) return;

    addHotTableCellDataUndoAction(hotInstance, cellDataChanges, TableChangeSource.CELL_META_UPDATE_CELL_TEXT_STYLE);
    applyExactHotTableCellDataChanges(hotInstance, cellDataChanges, TableChangeSource.CELL_META_UPDATE_CELL_TEXT_STYLE);
    hotInstance.runHooks(ON_MILANOTE_MARK_DIRTY_AND_FLUSH, TableChangeSource.CELL_META_UPDATE_CELL_TEXT_STYLE);
};
